import { logger } from '@remente/shared-frontend';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  getAdditionalUserInfo,
  AuthProvider as FirebaseAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup,
  User,
  UserCredential,
  NextOrObserver,
} from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';

import { initializeOnboarding } from './onboarding';

const firebaseConfig = {
  projectId: import.meta.env.VITE_APP_firebaseProjectId,
  appId: import.meta.env.VITE_APP_firebaseAppId,
  apiKey: import.meta.env.VITE_APP_firebaseApiKey,
  authDomain: import.meta.env.VITE_APP_firebaseAuthDomain,
  storageBucket: import.meta.env.VITE_APP_firebaseStorageBucket,
  messagingSenderId: import.meta.env.VITE_APP_firebaseMessagingSenderId,
  databaseURL: import.meta.env.VITE_APP_firebaseDatabaseURL,
};

Object.entries(firebaseConfig).forEach(([key, val]) => {
  if (val == null) {
    logger.error(key + ' env var is undefined!');
  }
});

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Providers
const googleProvider = new GoogleAuthProvider();
const facebookProvider = new FacebookAuthProvider();
const appleProvider = new OAuthProvider('apple.com');

// Default home content ID to set for new users
const DEFAULT_HOME_CONTENT_ID = 'eba16237-ec8f-4723-bdab-15170aea6d67';

/**
 * Sets home content field for a new user
 * Note: This should only be called after verifying the user is actually new
 * Uses merge to upsert only the homeContent field without affecting other fields
 */
const createHomeContentForNewUser = async (user: User): Promise<void> => {
  try {
    const contentRef = doc(db, 'content', user.uid);

    await setDoc(
      contentRef,
      {
        homeContent: [DEFAULT_HOME_CONTENT_ID],
      },
      { merge: true }
    );
    logger.log('Home content set for user:', user.uid);
  } catch (error) {
    logger.error('Error setting home content:', error);
    // Don't throw - we don't want to fail signup if Firestore write fails
  }
};

const finishAuthentication = (result: UserCredential, isNewUser: boolean) => {
  if (isNewUser) {
    // Optional app setup must not delay or fail authentication and code redemption.
    void initializeOnboarding(result.user.uid).catch((error) => {
      logger.error('Error initializing app onboarding:', error);
    });
    void createHomeContentForNewUser(result.user);
  }
  return result.user;
};

export const signup = async (email: string, password: string) => {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  return finishAuthentication(result, true);
};

export const login = async (email: string, password: string) => {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return finishAuthentication(result, false);
};

const signInWithProvider = async (provider: FirebaseAuthProvider) => {
  const result = await signInWithPopup(auth, provider);
  // Creation/sign-in timestamps can match for an existing identity; use Firebase's result.
  return finishAuthentication(result, getAdditionalUserInfo(result)?.isNewUser === true);
};

export const signInWithGoogle = () => signInWithProvider(googleProvider);
export const signInWithFacebook = () => signInWithProvider(facebookProvider);
export const signInWithApple = () => signInWithProvider(appleProvider);

export const logout = async () => {
  try {
    await auth.signOut();
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

export const resetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

export const onAuthStateChanged = (callback: NextOrObserver<User | null>) => {
  return auth.onAuthStateChanged(callback);
};
